import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface ComplianceArea {
  id: string
  area: string
  status: string
  score: number
  last_audit: string
  next_audit: string
  findings: number
  critical_findings: number
}

interface Audit {
  id: string
  name: string
  type: string
  status: string
  date: string
  findings: number
  remediation_status: string
}

interface Statistics {
  compliance_score: number
  areas_compliant: number
  areas_at_risk: number
  open_findings: number
  audits_this_quarter: number
  remediation_rate: number
}

export default function ComplianceMonitoringPage() {
  const [areas, setAreas] = useState<ComplianceArea[]>([])
  const [audits, setAudits] = useState<Audit[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [areasRes, auditsRes, statsRes] = await Promise.all([
        fetch('/api/compliancemonitoring/areas'),
        fetch('/api/compliancemonitoring/audits'),
        fetch('/api/compliancemonitoring/statistics')
      ])
      if (areasRes.ok) setAreas(await areasRes.json())
      if (auditsRes.ok) setAudits(await auditsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching compliance monitoring data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    if (status === 'compliant') return 'bg-green-100 text-green-800'
    if (status === 'review_needed') return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  const getAuditStatusBadge = (status: string) => {
    if (status === 'completed') return 'bg-green-100 text-green-800'
    if (status === 'in_progress') return 'bg-blue-100 text-blue-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Compliance Monitoring</h1>
          <p className="mt-2 text-gray-600">Phase 253 - Regulatory compliance and audit management</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Compliance Score</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.compliance_score}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Areas Compliant</p>
              <p className="text-2xl font-bold text-green-600">{statistics.areas_compliant}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">At Risk</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.areas_at_risk}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Open Findings</p>
              <p className="text-2xl font-bold text-red-600">{statistics.open_findings}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Audits (Q)</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.audits_this_quarter}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Remediation</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.remediation_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Compliance Areas</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Area</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Audit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Audit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Findings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {areas.map((area) => (
                  <tr key={area.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{area.area}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(area.status)}`}>{area.status}</span>
                    </td>
                    <td className="px-6 py-4 text-blue-600 font-bold">{area.score}</td>
                    <td className="px-6 py-4 text-gray-500">{area.last_audit}</td>
                    <td className="px-6 py-4 text-gray-500">{area.next_audit}</td>
                    <td className="px-6 py-4">
                      <span className="text-gray-900">{area.findings}</span>
                      {area.critical_findings > 0 && (
                        <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">{area.critical_findings} critical</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Audit Activities</h2>
          </div>
          <div className="p-6 space-y-4">
            {audits.map((audit) => (
              <div key={audit.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-900">{audit.name}</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getAuditStatusBadge(audit.status)}`}>{audit.status}</span>
                  </div>
                  <span className="text-sm text-gray-500">{audit.date}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Type</p>
                    <p className="font-medium">{audit.type}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Findings</p>
                    <p className="font-medium text-blue-600">{audit.findings}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Remediation</p>
                    <p className="font-medium text-green-600">{audit.remediation_status}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
